import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Category, CreateCategoryForm, UpdateCategoryForm } from '../../types';

interface CategoryFormProps {
  category?: Category | null;
  onSubmit: (data: CreateCategoryForm | UpdateCategoryForm) => void;
  isLoading?: boolean;
  error?: string;
}

const categorySchema = yup.object({
  name: yup.string().required('Name is required').max(100, 'Name must be less than 100 characters'),
  description: yup.string().max(255, 'Description must be less than 255 characters'),
  isActive: yup.boolean().required()
}).required();

const CategoryForm: React.FC<CategoryFormProps> = ({ category, onSubmit, isLoading = false, error }) => {
  // Use category ID as key to force re-render when category changes
  const formKey = category?.id || 'new';
  const {
    register,
    handleSubmit,
    control,
    formState: { errors }
    // reset // Unused
  } = useForm<CreateCategoryForm>({
    resolver: yupResolver(categorySchema),
    defaultValues: {
      name: category?.name || '',
      description: category?.description || '',
      isActive: category?.isActive ?? true
    },
    key: formKey // Force re-initialization when category changes
  });

  const handleFormSubmit = (data: CreateCategoryForm) => {
    onSubmit(data);
  };



  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            {...register('name')}
            type="text"
            id="name"
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
            placeholder="Enter category name"
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            {...register('description')}
            id="description"
            rows={3}
            className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-accent-color focus:border-accent-color"
            placeholder="Enter category description"
          />
          {errors.description && (
            <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
          )}
        </div>

        {/* Is Active */}
        <div>
          <Controller
            name="isActive"
            control={control}
            render={({ field: { onChange, value, ref } }) => (
              <div className="flex items-center">
                <input
                  ref={ref}
                  type="checkbox"
                  id="isActive"
                  checked={value}
                  onChange={(e) => onChange(e.target.checked)}
                  className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>
            )}
          />
          {errors.isActive && (
            <p className="mt-1 text-sm text-red-600">{errors.isActive.message}</p>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              {category ? 'Updating...' : 'Creating...'}
            </div>
          ) : (
            category ? 'Update Category' : 'Create Category'
          )}
        </button>
      </div>
    </form>
  );
};

export default CategoryForm;
